import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../lib/baseQueries";
import type { IContent } from "@/features/ContentTable/ContentTable.types";
import { EPublicationStatus } from "@/types/commons";

interface ThumbnailPresignedUrlResponse {
  url: string;
  key: string;
  expiresIn: number;
}

interface ThumbnailAssetResponse {
  id: string;
  key: string;
  filename: string;
  extension: string;
  mimeType: string;
  size: number;
  uploadedById: string;
}

export interface IYoutubeMetaPayload {
  title: string;
  description: string;
  thumbnailId: string;
  tags?: string;
  categoryId?: string;
  privacyStatus?: string;
  scheduledPublishAt?: string | null;
  madeForKids?: boolean;
  defaultLanguage?: string;
  defaultAudioLanguage?: string;
  license?: string;
  embeddable?: boolean;
  playlistId?: string;
  ageRestricted?: boolean;
  hasPaidProductPlacement?: boolean;
  containsSyntheticMedia?: boolean;
  allowAutomaticChapters?: boolean;
  allowAutomaticPlaces?: boolean;
  allowAutomaticConcepts?: boolean;
  captionCertification?: string;
  recordingDate?: string | null;
  videoLocation?: string;
  notifySubscribers?: boolean;
  shortsRemixing?: string;
  gameTitle?: string;
  showLikes?: boolean;
}

export const contentApi = createApi({
  reducerPath: "contentApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Content", "YoutubePlaylists"],
  endpoints: (builder) => ({
    getContentById: builder.query<IContent, string>({
      query: (contentId) => `/v1/contents/${contentId}`,
      providesTags: (_result, _error, id) => [{ type: "Content", id }],
    }),

    getThumbnailPresignedUrl: builder.mutation<
      ThumbnailPresignedUrlResponse,
      {
        workspaceId: string;
        contentId: string;
        contentType: string;
        extension: string;
        size: number;
      }
    >({
      query: (body) => ({
        url: "/v1/assets/thumbnail/presigned-url",
        method: "POST",
        body,
      }),
    }),

    confirmThumbnailUpload: builder.mutation<
      ThumbnailAssetResponse,
      {
        key: string;
        filename: string;
        extension: string;
        mimeType: string;
        size: number;
      }
    >({
      query: (body) => ({
        url: "/v1/assets/thumbnail/confirm",
        method: "POST",
        body,
      }),
    }),

    getPublication: builder.query<
      any,
      { contentId: string; workspaceId: string }
    >({
      query: ({ contentId, workspaceId }) =>
        `/v1/contents/${contentId}/publications?workspaceId=${workspaceId}`,
      providesTags: (_result, _error, { contentId }) => [
        { type: "Content", id: contentId },
      ],
    }),

    getYoutubePlaylists: builder.query<
      { id: string; title: string }[],
      string
    >({
      query: (workspaceId) => `/v1/workspaces/${workspaceId}/playlists`,
      providesTags: ["YoutubePlaylists"],
    }),

    createYoutubePlaylist: builder.mutation<
      { id: string; title: string },
      {
        workspaceId: string;
        title: string;
        description?: string;
        privacyStatus?: string;
        defaultLanguage?: string;
      }
    >({
      query: ({ workspaceId, ...body }) => ({
        url: `/v1/workspaces/${workspaceId}/playlists`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["YoutubePlaylists"],
    }),

    createPublication: builder.mutation<
      any,
      {
        contentId: string;
        workspaceId: string;
        status: EPublicationStatus;
        meta: IYoutubeMetaPayload;
      }
    >({
      query: ({ contentId, ...body }) => ({
        url: `/v1/contents/${contentId}/publications`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { contentId }) => [
        { type: "Content", id: contentId },
      ],
    }),

    rejectReview: builder.mutation<
      any,
      { reviewId: string; comment: string; contentId: string }
    >({
      query: ({ reviewId, comment }) => ({
        url: `/v1/reviews/${reviewId}/reject`,
        method: "POST",
        body: { comment },
      }),
      invalidatesTags: (_result, _error, { contentId }) => [
        { type: "Content", id: contentId },
      ],
    }),

    publishReview: builder.mutation<
      any,
      { reviewId: string; contentId: string }
    >({
      query: ({ reviewId }) => ({
        url: `/v1/reviews/${reviewId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { contentId }) => [
        { type: "Content", id: contentId },
      ],
    }),

    directPublish: builder.mutation<
      any,
      { contentPublicationId: string; metaId: string; contentId: string }
    >({
      query: ({ contentPublicationId, metaId }) => ({
        url: `/v1/reviews/direct-publish`,
        method: "POST",
        body: { contentPublicationId, metaId },
      }),
      invalidatesTags: (_result, _error, { contentId }) => [
        { type: "Content", id: contentId },
      ],
    }),
  }),
});

export const {
  useGetContentByIdQuery,
  useGetThumbnailPresignedUrlMutation,
  useConfirmThumbnailUploadMutation,
  useGetPublicationQuery,
  useGetYoutubePlaylistsQuery,
  useCreateYoutubePlaylistMutation,
  useCreatePublicationMutation,
  useRejectReviewMutation,
  usePublishReviewMutation,
  useDirectPublishMutation,
} = contentApi;

